import React, { FC, Dispatch, SetStateAction, useState, useEffect } from "react"
import { Table } from "@/components/general/Table"
import { Geo, Order, PricingType, Quote, ReQuote } from "@/lib/types"
import {
  Localstorage,
  convertEquipmentType,
  dateFormat,
  dateFormat1,
  dollarUS,
} from "@/lib/services"
import { LocalStorage } from "@/lib/types/LocalStorage"
import { useRouter } from "next/router"
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline"
import { Tabs } from "@/components/general/Tabs"
import { Modal } from "@/components/general/Modal"
import { ReactMultiEmail } from "react-multi-email"
import { InputError } from "@/components/general/InputError"
import { useForm } from "react-hook-form"
import { dateFormat2, dateFormat3 } from "@/lib/services/Format"
import Swal from "sweetalert2"
import { api } from "@/lib/api"
import { useMutation } from "react-query"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  recentQuoteslistingData: { quotes: ReQuote[] }
  listingData: { deliveries: ReQuote[] }
  header: {
    name: string
    width: string
  }[]
  page?: number
  maxPage?: number
  quotepage?: number
  maxPageRecentQuotes?: number
  next: (identifier: string, newPage: number) => void
  previous: (identifier: string, newPage: number) => void
  selectedTab: string
  setSelectedTab: Dispatch<SetStateAction<string>>
}

const tabs = [
  {
    identifier: "quotes",
    name: "Quotes",
  },
  {
    identifier: "tendered",
    name: "Order Confirmations",
  },
]

export const RecentOrder: FC<Props> = (props) => {
  const user = Localstorage.get<LocalStorage>("next.user")
  const router = useRouter()
  const handleReQuote = (e: ReQuote) => {
    const today = new Date().toISOString().split("T")[0];
    const pickup = new Date(e.pickupDate).toISOString().split("T")[0];
    // const todayStr = today.toISOString().split("T")[0];
    // const pickupStr = pickup.toISOString().split("T")[0];
    const validPickupDateStr = pickup < today ? today : pickup;

    const data = {
      equipmentType: e.equipment,
      originZip: e.location.origin.zipCode,
      destinationZip: e.location.destination.zipCode,
      pickUpDate: dateFormat2(validPickupDateStr), 
      originLatLng: {
        city: e.location.origin.city,
        country: e.location.origin.country || "US",
        state: e.location.origin.state,
        zip: e.location.origin.zipCode,
        location: {
          lat: e.location.origin.location.lat,
          lng: e.location.origin.location.lng,
        },
      },
      destinationLatLng: {
        city: e.location.destination.city,
        country: e.location.destination.country || "US",
        state: e.location.destination.state,
        zip: e.location.destination.zipCode,
        location: {
          lat: e.location.destination.location.lat,
          lng: e.location.destination.location.lng,
        },
      },
    };

    if (user?.role === "CUSTOMER") {
      localStorage.setItem("re_quote", JSON.stringify(data));
      Localstorage.set("re_quote_equip", data.equipmentType);
      router.push("/customer-dashboard");
    } else {
      localStorage.setItem("re_quote", JSON.stringify(data));
      Localstorage.set("re_quote_equip", data.equipmentType);
      router.push("/sales-dashboard");
    }
  };


  const [email, setEmail] = useState<boolean>(false)
  const [emails, setEmails] = useState<string[]>([])
  const [emailError, setEmailError] = useState("")
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<PricingType>()
  const populateData = () => {
    setEmail(true)
  }
  const [selectedRow, setSelectedRow] = useState<any | null>(null)
  const sentEmailMutation = useMutation(
    (payload: PricingType) => {
      return api.SalesApi.Send(payload)
    },
    {
      onSuccess() {
        setEmail(false)
        Swal.fire({
          icon: "success",
          title: "Successfully Sent Email",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          },
        })
      },
      onError(error: { message: string }) {
        Swal.fire({
          icon: "error",
          text: error.message,
          color: "#ffffff",
          background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        })
      },
    },
  )
  const onSubmit = (data: PricingType) => {
    data.report = false
    data.email = emails
    data.portOfEntry = ""
    data.orderType = "Standard"
    if (data.pickupDate && data.deliveryDate) {
      const pickup = new Date(data.pickupDate)
      const delivery = new Date(data.deliveryDate)
      const diffTime = delivery.getTime() - pickup.getTime()
      data.transitDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    }
    if (data.email.length == 0) {
      setEmailError("Please select one or more emails.")
      // } else if (data.pickupDate && data.deliveryDate) {
      //   data.pickupDate = dateFormat(data.pickupDate).split("T")[0]
      //   data.deliveryDate = dateFormat(datadeliveryDate).split("T")[0]
    } else {
      sentEmailMutation.mutate(data)
      // console.log(data,'data')
    }
  }
  useEffect(() => {
    if (selectedRow) {
      reset({
        origin: selectedRow.origin || "",
        destination: selectedRow.destination || "",
        equipmentType: convertEquipmentType(selectedRow.equipment) || "",
        quotePrice: (selectedRow.price as any) || "",
        pickupDate: selectedRow.pickupDate || "",
        deliveryDate: selectedRow.deliveryDate || "",
        mileage: selectedRow.mileage || "",
        quoteId: selectedRow.quoteId || "",
      })
    }
  }, [selectedRow, reset])

  const isDateOld = (date: string) => {
    const twoDaysAgo = new Date()
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2)
    return new Date(date) < twoDaysAgo
  }

  const handleEmail = (e: ReQuote) => {
    const isPickupDateOld = isDateOld(e.createdAt)
    if (isPickupDateOld) return
    setSelectedRow({
      origin: e.origin,
      destination: e.destination,
      equipment: e.equipment,
      price: e.price,
      pickupDate: e.pickupDate,
      deliveryDate: e.deliveryDate,
      mileage: e.mileage,
      quoteId: e.quoteId
    })
    setEmails([])
    populateData()
    setEmailError("")
  }

  return (
    <>
      <Modal
        open={email}
        close={() => setEmail(false)}
        width="small"
        title="Email"
      >
        <div className="flex flex-col gap-3">
          <div>
            <h5 className="text-xs dark:text-primary-8 text-light-1">Email</h5>
            <ReactMultiEmail
              placeholder="To..."
              emails={emails}
              onChange={(_emails: string[]) => {
                setEmailError("")
                setEmails(_emails)
              }}
              autoFocus={true}
              getLabel={(email, index, removeEmail) => {
                return (
                  <div data-tag key={index}>
                    <div data-tag-item>{email}</div>
                    <span data-tag-handle onClick={() => removeEmail(index)}>
                      ×
                    </span>
                  </div>
                )
              }}
            />
            {emailError !== "" && (
              <div className="pt-0.5">
                <InputError message={emailError} />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <fieldset className="pt-1">
              <label
                className="text-xs dark:text-primary-8 text-light-1"
                htmlFor=""
              >
                Origin
              </label>
              <input
                className={
                  (errors.origin
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded"
                }
                type="text"
                {...register("origin", { required: true })}
              />
            </fieldset>
            <fieldset className="pt-1">
              <label
                className="text-xs dark:text-primary-8 text-light-1"
                htmlFor=""
              >
                Destination
              </label>
              <input
                className={
                  (errors.destination
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded"
                }
                type="text"
                {...register("destination", { required: true })}
              />
            </fieldset>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <fieldset className="pt-1">
              <label
                className="text-xs dark:text-primary-8 text-light-1"
                htmlFor=""
              >
                Equipment Type
              </label>
              <input
                className={
                  (errors.equipmentType
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded"
                }
                type="text"
                {...register("equipmentType", { required: true })}
              />
            </fieldset>
            <fieldset className="pt-1">
              <label
                className="text-xs dark:text-primary-8 text-light-1"
                htmlFor=""
              >
                Quote Price
              </label>
              <input
                className={
                  (errors.quotePrice
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded"
                }
                type="text"
                {...register("quotePrice", { required: true })}
              />
            </fieldset>
          </div>
          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full dark:text-secondary-1 text-primary-1"
            onClick={handleSubmit(onSubmit)}
            type="button"
          >
            Send Quote
          </button>
        </div>
      </Modal>
      <div className="md:mx-4 mx-2 mt-2">
        <div className="pb-2">
          <Tabs
            selectTab={props.setSelectedTab}
            tabs={tabs}
            selectedTab={props.selectedTab}
          />
        </div>
        {props.selectedTab == "quotes" ? (
          <>
            <Table
              header={
                user?.role === "CUSTOMER"
                  ? props.header.filter(
                    (items) =>
                      items.name !== "Cost" &&
                      items.name !== "Delivery Date" &&
                      items.name !== "Method",
                  )
                  : props.header.filter(
                    (items) =>
                      items.name !== "Delivery Date" &&
                      items.name !== "Method",
                  )
              }
              headerAlign="text-left"
              lastheadingAlign="text-right"
            >
              {props.recentQuoteslistingData.quotes.map((e, i) => (
                <tr
                  key={i}
                  className="dark:text-primary-10 text-sm border-b-2 dark:border-secondary-11 text-left"
                >
                  <td className="whitespace-nowrap p-4 w-28">
                    {dateFormat1(e.createdAt)}
                  </td>
                  <td className="whitespace-nowrap p-4 w-52">
                    {e.user.firstName} {e.user.lastName}
                  </td>
                  <td className="whitespace-nowrap p-4 w-64 truncate">
                    {e.origin}
                  </td>
                  <td className="whitespace-nowrap p-4 w-64 truncate">
                    {e.destination}
                  </td>
                  <td className="whitespace-nowrap p-4 w-40">
                    {convertEquipmentType(e.equipment)}
                  </td>
                  <td className="whitespace-nowrap p-4 w-44">
                    {dateFormat1(e.pickupDate)}
                  </td>
                  {user?.role != "CUSTOMER" && (
                    <td className="whitespace-nowrap p-4 w-28">
                      {dollarUS.format(e.cost)}
                    </td>
                  )}
                  <td className="whitespace-nowrap p-4 w-28">
                    {dollarUS.format(e.price)}
                  </td>
                  <td className={"whitespace-nowrap p-4 w-24"}>
                    <div className="flex justify-end gap-2">
                      <ArrowPathIcon
                        className="w-4 h-4 dark:text-primary-8 text-light-1 cursor-pointer"
                        onClick={() => handleReQuote(e)}
                      />
                      {user?.role != "CUSTOMER" && (
                        <EnvelopeIcon
                          className={
                            (isDateOld(e.createdAt)
                              ? "dark:text-primary-8/50 text-light-1/70"
                              : "dark:text-primary-8 text-light-1") +
                            " w-4 h-4 cursor-pointer"
                          }
                          onClick={() => handleEmail(e)}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </>
        ) : props.selectedTab == "tendered" ? (
          <>
            <Table
              header={
                user?.role === "CUSTOMER"
                  ? props.header.filter((Order) => Order.name !== "Cost")
                  : props.header
              }
              headerAlign="text-left"
              lastheadingAlign="text-right"
            >
              {props.listingData.deliveries.map((e, i) => (

                <tr key={i} className="text-left">
                  <td className="whitespace-nowrap p-4 w-28">
                    {dateFormat1(e.createdAt)}
                  </td>

                  <td className="whitespace-nowrap p-4 w-52">
                    {e.user.firstName} {e.user.lastName}
                  </td>
                  <td className="whitespace-nowrap p-4 w-64 truncate">
                    {e.origin}
                  </td>
                  <td className="whitespace-nowrap p-4 w-64 truncate">
                    {e.destination}
                  </td>
                  <td className="whitespace-nowrap p-4 w-40">
                    {convertEquipmentType(e.equipment)}
                  </td>
                  <td className="whitespace-nowrap p-4 w-44">
                    {dateFormat1(e.pickupDate)}
                  </td>
                  <td className="whitespace-nowrap p-4 w-44">
                    {dateFormat1(e.deliveryDate)}
                  </td>
                  <td className="whitespace-nowrap p-4 w-36">
                    {/* {e.method} */}
                    {e.method === 'Standard' ? 'Domestic' :
                      e.method?.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).replace('Rates', '')}
                  </td>
                  {user?.role != "CUSTOMER" && (
                    <td className="whitespace-nowrap p-4 w-28">
                      {dollarUS.format(e.cost)}
                    </td>
                  )}
                  <td className="whitespace-nowrap p-4 w-28">
                    {dollarUS.format(e.price)}
                  </td>

                  <td className={"whitespace-nowrap p-4 w-24"}>
                    <div className="flex justify-end gap-2">
                      <ArrowPathIcon
                        className="w-4 h-4 dark:text-primary-8 text-light-1 cursor-pointer"
                        onClick={() => handleReQuote(e)}
                      />
                      {/* {user?.role != "CUSTOMER" && (
                        <EnvelopeIcon
                          className={
                            (isDateOld(e.createdAt)
                              ? "dark:text-primary-8/50 text-light-1/70"
                              : "dark:text-primary-8 text-light-1") +
                            " w-4 h-4 cursor-pointer"
                          }
                          onClick={() => handleEmail(e)}
                        />
                      )} */}
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </>
        ) : null}
      </div>
    </>
  )
}

export default RecentOrder
